/**
 * `createGame` — a package plus a difficulty profile plus a seed, resolved into
 * an opening `GameState`.
 *
 * This is the only place a difficulty profile is *read*. Everything the profile
 * says is resolved into `state.settings` here, and every later decision
 * consults `state.settings` rather than the profile. That is what makes a
 * mid-mission difficulty switch a matter of recomputing one object, and what
 * stops the engine ever branching on a profile's name (design D7).
 */
import { CAPS, } from '@managing-up/schema';
import { sortedUnique } from './collections.js';
import { activeSlotDefs, admitsParticle } from './composer.js';
import { loadPack } from './pack.js';
import { applyPerkEffects, equippedPerks, packWithPerks, perkParticleIds, phaseBudgetDeltaOf, } from './perks.js';
import { aggregateTagSets } from './tags.js';
import { enterPhase } from './world.js';
import { STATE_VERSION, } from './state.js';
/** Puppet slots held at once when the profile document does not say otherwise. */
export const DEFAULT_BANDWIDTH_CAPACITY = 3;
/**
 * Resolves a profile into the flat modifier set the engine consults.
 *
 * The phase budget is *derived* rather than read, and the base it is derived
 * from is the **larger** of the solver's measured par and the package's own
 * declared budget. Both halves of that matter:
 *
 *   - **par, when it is the larger.** A mission the solver cannot finish inside
 *     the budget its author guessed at is a mission whose author guessed wrong,
 *     and scaling their guess would ship something unwinnable;
 *   - **the declared budget, when *it* is the larger.** Par is the optimum, and
 *     multiplying an optimum only expresses generosity while the optimum is
 *     big. Mission 01 is solvable in a single phase; scaling that by the
 *     profiles gives Intern two phases, Analyst two, and Partner **one** — a
 *     mission that ends the first time a player says "let's pick this up after
 *     lunch". Measured in phase 18: at Partner the naive agent lost every run
 *     to a clock it had spent one move of. The author declared four phases, and
 *     four is what the mission was built and written around.
 *
 * So a profile's multiplier scales the mission as designed, and par raises the
 * floor when the design turns out to be too tight. Nothing downstream asks
 * which of the two won — `settings.parPhases` records the base so a mid-mission
 * difficulty switch re-derives from the same number.
 */
export function resolveSettings(pack, profile, options = {}, perks = []) {
    const parPhases = Math.max(1, Math.min(Math.max(options.parPhases ?? 0, pack.objectives.phaseBudget), CAPS.phaseBudget));
    // A perk that buys back some of the week does so before the multiplication,
    // not after: it widens the profile's generosity rather than adding phases on
    // top of it, so Partner's tighter clock still bites through the perk.
    const budgetMultiplier = Math.max(0.1, profile.phaseBudgetMultiplier + phaseBudgetDeltaOf(perks));
    const base = {
        profileId: profile.id,
        tagChips: profile.tagChips,
        decoyTierMax: profile.decoyTierMax,
        surfaceExtraDecoys: profile.surfaceExtraDecoys,
        strikeBudget: profile.strikeBudget,
        disasterUndoAllowance: profile.disasterUndoAllowance,
        parPhases,
        phaseBudget: Math.max(1, Math.ceil(parPhases * budgetMultiplier)),
        feedbackVerbosity: profile.feedbackVerbosity,
        hintThreshold: profile.hintThreshold,
        noiseBurial: profile.noiseBurial,
        attentionDecayEnabled: profile.attentionDecay.enabled,
        attentionDecayMultiplier: profile.attentionDecay.rateMultiplier,
        suspicionMultiplier: profile.suspicionMultiplier,
        voiceMaskPenalty: profile.voiceMaskPenalty,
        scoreMultiplier: profile.scoreMultiplier,
        bandwidthCapacity: options.bandwidthCapacity ?? DEFAULT_BANDWIDTH_CAPACITY,
        maxTargetsPerPhase: Math.min(pack.objectives.maxTargetsPerPhase, CAPS.targetsPerPhase),
        equippedPerks: [],
        blocksAttributionMutation: false,
        overridesNodeTemperature: false,
    };
    return applyPerkEffects(base, perks);
}
/**
 * The particles the player opens with: everything sourced from the starter pool
 * that the profile's decoy ceiling admits. Harvested, granted, and persona
 * particles arrive later, through play.
 *
 * Exported because a mid-mission profile switch has to ask the same question
 * again: raising the ceiling has to hand the player the starter decoys the
 * lower profile withheld, or the two profiles would not be the same mission.
 */
export function starterParticles(pack, settings, perkParticles) {
    return sortedUnique([
        ...pack.particles
            .filter((particle) => particle.source === 'starter' && admitsParticle(settings, particle))
            .map((particle) => particle.id),
        // The perk's own particle is in the player's pocket, not the mission's:
        // it opens unlocked in every mission, at every profile.
        ...perkParticles,
    ]);
}
/** Builds the opening state from an indexed package and a resolved settings set. */
function initialState(loaded, settings, seed, options, perkParticles) {
    const pack = loaded.pack;
    const channels = {};
    const unlockedChannels = [];
    for (const channel of pack.channels) {
        // A channel opens locked when it needs a puppet, or when the player simply
        // is not in it. Locked channels are still listed — the sidebar explains why.
        const unlocked = channel.requiresPuppet === undefined && channel.members.includes('player');
        channels[channel.id] = { id: channel.id, unlocked, unread: 0 };
        if (unlocked)
            unlockedChannels.push(channel.id);
    }
    const messages = pack.backlog.map((message, index) => ({
        id: message.id,
        channelId: message.channel,
        authorNodeId: message.authorNodeId,
        text: message.text,
        timestamp: message.timestamp,
        reactions: message.reactions ?? [],
        threadParentId: message.threadParentId ?? null,
        highlights: message.highlights ?? [],
        identity: null,
        day: 1,
        phase: 'morning',
        ordinal: index,
        origin: 'backlog',
    }));
    const nodes = {};
    for (const node of pack.cast)
        nodes[node.id] = { vars: { ...node.stateVars } };
    // `player` is addressable by `stateDeltas` and readable by `nodeState`
    // predicates, but is not in the cast — the player is not an NPC.
    nodes['player'] = { vars: {} };
    const puppets = {};
    const unlockedPuppets = [];
    for (const puppet of pack.puppets) {
        const trust = nodes[puppet.nodeId]?.vars['trust'] ?? 0;
        const unlocked = trust >= puppet.trustThreshold;
        puppets[puppet.nodeId] = {
            nodeId: puppet.nodeId,
            unlocked,
            possessed: false,
            credibility: puppet.credibility.initial,
            lockedUntilDay: null,
            lastActiveDay: null,
        };
        if (unlocked)
            unlockedPuppets.push(puppet.nodeId);
    }
    const slots = activeSlotDefs(loaded, 'self').map((slot) => ({
        n: slot.n,
        role: slot.role,
        required: slot.required,
        particleId: null,
    }));
    return {
        stateVersion: STATE_VERSION,
        packId: pack.manifest.id,
        packVersion: pack.manifest.packVersion,
        seed,
        settings,
        clock: { day: 1, phase: 'morning', tick: 0, phasesUsed: 0 },
        activeChannelId: unlockedChannels[0] ?? null,
        channels,
        messages,
        messageOrdinal: messages.length,
        nodes,
        puppets,
        identity: 'self',
        bandwidth: { capacity: settings.bandwidthCapacity, used: 0, pendingNodeId: null },
        unlocked: {
            particles: starterParticles(pack, settings, perkParticles),
            channels: sortedUnique(unlockedChannels),
            puppets: sortedUnique(unlockedPuppets),
        },
        harvested: [],
        composer: { slots, targetNodeId: null },
        staged: [],
        proposal: {
            id: pack.proposal.id,
            tags: aggregateTagSets([pack.proposal.initialTags]),
            origin: 'player',
            attribution: pack.proposal.attribution,
            hops: 0,
            visited: [],
            holderNodeId: null,
            launched: false,
        },
        inFlight: [],
        pendingPosts: [],
        flags: [],
        pip: { strikes: 0, composerLockTicks: 0, openerToneRequired: null },
        reputation: { nearMissRuns: {}, peakSuspicion: 0, suspects: [] },
        cred: options.startingCred ?? 0,
        progress: {
            sends: 0,
            goldenSends: 0,
            nonGoldenSends: 0,
            disasters: 0,
            undosUsed: 0,
            hintsUsed: 0,
            credibilityLosses: 0,
            targetsThisPhase: [],
            assisted: options.assisted ?? false,
        },
        lastTrace: null,
        undoOffer: null,
        end: null,
    };
}
/**
 * Loads a package and opens a run against it.
 *
 * Returns the indexed package alongside the state because `reduce` needs both:
 * the state is the plain JSON that gets saved and hashed, and the package is
 * the immutable content it is interpreted against. Keeping them apart is what
 * lets a save be a few kilobytes rather than a copy of the mission.
 *
 * Day 1's morning is a phase like any other, so it is *entered* here: its
 * scheduled events fire and its chatter arrives before the player is handed the
 * composer. A mission whose opening flurry grants the particle the first pitch
 * needs would otherwise open unwinnable.
 */
export function createGame(pack, profile, seed, options = {}) {
    const perks = options.perks === undefined ? [] : equippedPerks(options.perks, options.equipped ?? []);
    // Perk particles are spliced in *before* the pack is loaded, so they are
    // indexed, vocabulary-checked, and offered by the picker exactly like the
    // mission's own — there is no second particle pool anywhere in the engine.
    const withPerks = packWithPerks(pack, perks);
    const loaded = loadPack(withPerks);
    const settings = resolveSettings(withPerks, profile, options, perks);
    const opening = enterPhase(loaded, initialState(loaded, settings, seed, options, perkParticleIds(perks)));
    return { loaded, state: opening.state, effects: opening.effects };
}
//# sourceMappingURL=create.js.map